import React, { useState, useEffect } from 'react';
import { Bot, Layers, Lightbulb } from 'lucide-react';
import { RunResult, StepType } from '../../types';
import { getStepTypeConfig } from '../../utils/stepTypeConfig';
import { apiService } from '../../services/api';
import RunResultErrorPanel from './RunResultErrorPanel';

interface InlineOutputDisplayProps {
  runResult: RunResult;
  onViewFullOutput: (runResult: RunResult) => void;
  maxPreviewLength?: number;
  showMetadata?: boolean;
  workflowSteps?: Array<{
    orchestration_step_id: string | number;
    step_type: string;
    description?: string;
    label?: string;
    agent_id?: number;
    config?: Record<string, any>;
  }>;
  agents?: Array<{ agent_id: number; name: string }>;
  onEditAgent?: (agentId: number, focusField: 'model') => void;
}

const InlineOutputDisplay: React.FC<InlineOutputDisplayProps> = ({
  runResult,
  onViewFullOutput,
  maxPreviewLength = 150,
  showMetadata = false,
  workflowSteps = [],
  agents = [],
  onEditAgent,
}) => {
  // Get queue information from step config
  const getQueueInfo = (step: typeof workflowSteps[0] | undefined) => {
    if (!step) return null;
    
    if (step.step_type === 'queue_write') {
      const queueId = step.config?.target_queue_id;
      const queueName = step.config?.target_queue_name;
      return { queueId, queueName, type: 'write' };
    } else if (step.step_type === 'queue_read') {
      const queueId = step.config?.source_queue_id;
      const queueName = step.config?.source_queue_name;
      return { queueId, queueName, type: 'read' };
    }
    return null;
  };

  // Get step and agent information
  const getStepInfo = () => {
    const step = workflowSteps.find(s => s.orchestration_step_id === runResult.orchestration_step_id);
    if (!step) return { typeInfo: null, agent: null, stepData: null, queueInfo: null };
    
    const typeInfo = step.step_type ? getStepTypeConfig(step.step_type as StepType) : null;
    const agent = step.agent_id ? agents.find(a => a.agent_id === step.agent_id) : null;
    const queueInfo = getQueueInfo(step);
    
    return { typeInfo, agent, stepData: step, queueInfo };
  };

  const { typeInfo: stepTypeInfo, agent, stepData, queueInfo } = getStepInfo();
  
  // State for fetched queue name (fallback for backward compatibility)
  const [fetchedQueueName, setFetchedQueueName] = useState<string | null>(null);
  const [isFetchingQueueName, setIsFetchingQueueName] = useState(false);
  
  // Fetch queue name from API if not in config (backward compatibility)
  useEffect(() => {
    if (queueInfo?.queueId && !queueInfo.queueName && !fetchedQueueName && !isFetchingQueueName) {
      setIsFetchingQueueName(true);
      apiService.getQueue(queueInfo.queueId)
        .then(queue => {
          setFetchedQueueName(queue.name);
        })
        .catch(error => {
          console.error('Failed to fetch queue name:', error);
          // Leave fetchedQueueName as null, will fallback to Queue #ID
        })
        .finally(() => {
          setIsFetchingQueueName(false);
        });
    }
  }, [queueInfo?.queueId, queueInfo?.queueName, fetchedQueueName, isFetchingQueueName]);
  
  // Reset fetched name when queue info changes
  useEffect(() => {
    setFetchedQueueName(null);
  }, [queueInfo?.queueId]);
  
  // Use Layers icon for queue steps (same as sidebar), otherwise use icon from config
  const StepIcon = (stepData?.step_type === 'queue_write' || stepData?.step_type === 'queue_read') 
    ? Layers 
    : (stepTypeInfo?.icon || Bot);
  const stepTypeName = stepTypeInfo?.name || 'Unknown';
  
  // Extract text color from stepTypeInfo.color (format: "bg-indigo-50 text-indigo-700")
  const getTextColor = () => {
    if (!stepTypeInfo?.color) return 'text-gray-600 dark:text-gray-400';
    const colorParts = stepTypeInfo.color.split(' ');
    const textColor = colorParts.find(part => part.startsWith('text-'));
    return textColor || 'text-gray-600 dark:text-gray-400';
  };
  const stepTypeColor = getTextColor();
  
  // Get queue display name
  const getQueueDisplayName = () => {
    if (!queueInfo) return null;
    // Priority: config name > fetched name > fallback to ID
    if (queueInfo.queueName) return queueInfo.queueName;
    if (fetchedQueueName) return fetchedQueueName;
    if (queueInfo.queueId) return `Queue #${queueInfo.queueId}`;
    return null;
  };
  
  const queueDisplayName = getQueueDisplayName();

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  const formatDuration = () => {
    // AC-1580: deterministic steps (filter, rdap) historically didn't
    // populate started_at on the RunResult row. ``new Date(null).getTime()``
    // returns 0 (Unix epoch), so a missing started_at made the duration
    // display as the entire Unix timestamp (~56 years). Guard against
    // null/undefined explicitly — only the formatted string is computed
    // when BOTH timestamps are present and parse cleanly.
    if (!runResult.completed_at || !runResult.started_at) return 'N/A';
    const start = new Date(runResult.started_at).getTime();
    const end = new Date(runResult.completed_at).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return 'N/A';
    return ((end - start) / 1000).toFixed(2) + 's';
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const hasLongOutput = runResult.final_output && 
    (typeof runResult.final_output === 'string' ? runResult.final_output.length > maxPreviewLength : true);

  const getOutputPreview = () => {
    if (!runResult.final_output) return 'No output';
    
    if (typeof runResult.final_output === 'string') {
      return runResult.final_output.length > maxPreviewLength
        ? runResult.final_output.substring(0, maxPreviewLength) + '...'
        : runResult.final_output;
    }
    
    try {
      const jsonString = JSON.stringify(runResult.final_output, null, 2);
      return jsonString.length > maxPreviewLength
        ? jsonString.substring(0, maxPreviewLength) + '...'
        : jsonString;
    } catch {
      return '[Unserializable Object]';
    }
  };

  const getFullOutput = () => {
    if (!runResult.final_output) return '';
    
    if (typeof runResult.final_output === 'string') {
      return runResult.final_output;
    }
    
    try {
      return JSON.stringify(runResult.final_output, null, 2);
    } catch {
      return '[Unserializable Object]';
    }
  };

  return (
    <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header with enhanced step and agent information */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-cap-dark">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
              runResult.status === 'completed' 
                ? 'bg-green-500' 
                : runResult.status === 'failed'
                ? 'bg-red-500'
                : 'bg-yellow-500'
            }`}>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {runResult.status === 'completed' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                ) : runResult.status === 'failed' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                )}
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              {/* Step type and agent row */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <StepIcon className={`w-4 h-4 ${stepTypeColor}`} />
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 px-2 py-0.5 bg-white dark:bg-cap-dark rounded-md border border-gray-200 dark:border-gray-700">
                    {stepTypeName}
                  </span>
                </div>
                {queueDisplayName && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-md border ${
                      stepData?.step_type === 'queue_write'
                        ? 'text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30 border-teal-200 dark:border-teal-800'
                        : 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800'
                    }`}>
                      {queueDisplayName}
                    </span>
                  </div>
                )}
                {agent && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                    <span className="text-xs font-medium text-indigo-600 dark:text-indigo-300 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-md border border-indigo-200 dark:border-indigo-800">
                      {agent.name}
                    </span>
                  </div>
                )}
                {stepData?.label && stepData.label !== agent?.name && stepData.label !== queueDisplayName && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                    <span className="text-xs text-gray-600 dark:text-gray-400 italic">
                      "{stepData.label}"
                    </span>
                  </div>
                )}
              </div>
              
              {/* Step order and status row */}
              <div className="flex items-center space-x-3">
                <span className="text-sm font-semibold text-gray-900 dark:text-warm-white bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                  Step {runResult.step_order}
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Status:</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    runResult.status === 'completed' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-300' 
                      : runResult.status === 'failed'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border border-red-300'
                      : runResult.status === 'running'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-primary-300'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600'
                  }`}>
                    {runResult.status === 'completed' ? '✓ Completed' : 
                     runResult.status === 'failed' ? '✗ Failed' :
                     runResult.status === 'running' ? '⟳ Running' :
                     runResult.status}
                  </span>
                </div>
                {runResult.completed_at && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Duration:</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {formatDuration()}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Hint application indicator - simplified */}
              {runResult.hint_application_details && runResult.hint_application_details.applied_hints && runResult.hint_application_details.applied_hints.length > 0 && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                  <Lightbulb className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                  <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                    {runResult.hint_application_details.applied_hints.length} Feedback Hint{runResult.hint_application_details.applied_hints.length !== 1 ? 's' : ''} Applied
                  </span>
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    (View Full for details)
                  </span>
                </div>
              )}
              
              {/* Output preview integrated into header */}
              {runResult.final_output && (
                <div className="mt-2">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">Output:</div>
                  <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded p-2 text-sm">
                    {getOutputPreview()}
                  </div>
                </div>
              )}
              
              {/* Error message if present */}
              {runResult.error_message && (
                <div className="mt-2">
                  <RunResultErrorPanel
                    runResult={runResult}
                    onEditAgent={onEditAgent}
                  />
                </div>
              )}
              
              {/* Metadata section - timestamps and details */}
              {showMetadata && (
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span>Started: {formatTimestamp(runResult.started_at)}</span>
                    {runResult.completed_at && (
                      <span>Completed: {formatTimestamp(runResult.completed_at)}</span>
                    )}
                  </div>
                  {runResult.input_type && (
                    <div>Input Type: <span className="text-gray-700 dark:text-gray-300 font-medium">{runResult.input_type}</span></div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0 ml-2 sm:ml-4">
            {runResult.final_output && (
              <button
                onClick={() => copyToClipboard(getFullOutput())}
                className="px-2 sm:px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors border border-gray-300 dark:border-gray-600"
              >
                Copy
              </button>
            )}
            {runResult.final_output && onViewFullOutput && (
              <button
                onClick={() => onViewFullOutput(runResult)}
                className="px-2 sm:px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-md transition-colors border border-primary-300 dark:border-blue-700"
              >
                View Full
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InlineOutputDisplay; 